/* ===================================================================
   TemplateGallery -- the "Browse templates" deep bench (Brad, 2026-06-29).

   A modal that lets an owner browse the 48 ready-made templates across 3
   industry packs and one-tap add any to their own list. WIRED to:
     GET  /api/v1/template-gallery        (browse + filter)
     POST /api/v1/templates/from-gallery  (add one to the tenant list)

   The add is idempotent server-side (201 fresh / 200 already there). After a
   successful add we invalidate the tenant templates query so the main
   Templates list shows the new one immediately (handled in useAddFromGallery).

   SMS previews reuse the iMessage-blue bubble (.bub.bub--imessage) so the
   channel reads as a text at a glance, matching the main list. Email previews
   show the subject line + body in the neutral .bub box. No raw ids / JSON are
   ever shown to the owner.
   =================================================================== */
import { useMemo, useState } from 'react';
import { Card, Chip, Icon, ClosePath } from './ui';
import type { ChipKind } from './ui';
import { useTemplateGallery, useAddFromGallery } from '../hooks';
import type { GalleryTemplate } from '../lib/api';
import '../styles/components.css';

/* ---- human labels (never show raw tags to the owner) ---- */
const PACK_LABELS: Record<string, string> = {
  trades: 'Trades & Home Services',
  real_estate: 'Real Estate',
  general: 'General',
};
const SCENARIO_LABELS: Record<string, string> = {
  after_job: 'After the job',
  reminder_1: 'Friendly nudge',
  reminder_2: 'Last nudge',
  first_time: 'First-time',
  repeat: 'Repeat/loyal',
  post_estimate: 'After estimate/showing',
  win_back: 'Win-back',
  seasonal: 'Seasonal',
};
function packLabel(p: string): string {
  return PACK_LABELS[p] ?? p;
}
function scenarioLabel(s: string): string {
  return SCENARIO_LABELS[s] ?? s;
}
function channelChip(channel: string): ChipKind {
  return channel === 'sms' ? 'blue' : 'gray';
}
function channelLabel(channel: string): string {
  return channel === 'sms' ? 'SMS' : 'Email';
}

const CheckSmall = <path d="M5 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />;

/* ---- a single filter pill row ---- */
function FilterRow({
  label,
  options,
  active,
  onPick,
}: {
  label: string;
  options: { value: string; label: string }[];
  active: string | null;
  onPick: (value: string | null) => void;
}) {
  return (
    <div className="gal-filter">
      <span className="gal-filter__lab">{label}</span>
      <div className="gal-filter__pills">
        <button
          className={'gal-pill' + (active === null ? ' is-active' : '')}
          onClick={() => onPick(null)}
        >
          All
        </button>
        {options.map((o) => (
          <button
            key={o.value}
            className={'gal-pill' + (active === o.value ? ' is-active' : '')}
            onClick={() => onPick(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function GalleryItemCard({
  item,
  onUse,
  adding,
}: {
  item: GalleryTemplate;
  onUse: (item: GalleryTemplate) => void;
  adding: boolean;
}) {
  return (
    <Card>
      <div className="card__head" style={{ alignItems: 'flex-start' }}>
        <h3 style={{ fontSize: 14.5 }}>{item.name}</h3>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Chip kind="gray">{packLabel(item.pack)}</Chip>
          <Chip kind={channelChip(item.channel)}>{channelLabel(item.channel)}</Chip>
        </span>
      </div>
      <div style={{ marginBottom: 12 }}>
        <Chip kind="gold">{scenarioLabel(item.scenario)}</Chip>
      </div>

      {item.channel === 'email' && item.subject && (
        <div className="gal-subject">
          <span className="gal-subject__lab">Subject</span>
          {item.subject}
        </div>
      )}
      <div className="msg you" style={{ marginBottom: 14 }}>
        <div
          className={item.channel === 'sms' ? 'bub bub--imessage' : 'bub'}
          style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}
        >
          {item.body}
        </div>
      </div>

      <button
        className="btn btn--primary"
        style={{ width: '100%', justifyContent: 'center' }}
        onClick={() => onUse(item)}
        disabled={adding}
      >
        {adding ? (
          'Adding...'
        ) : (
          <>
            <Icon d={CheckSmall} sw={2.2} /> Use this template
          </>
        )}
      </button>
    </Card>
  );
}

export function TemplateGallery({ onClose }: { onClose: () => void }) {
  const [pack, setPack] = useState<string | null>(null);
  const [channel, setChannel] = useState<string | null>(null);
  const [scenario, setScenario] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      ...(pack ? { pack } : {}),
      ...(channel ? { channel: channel as 'sms' | 'email' } : {}),
      ...(scenario ? { scenario } : {}),
    }),
    [pack, channel, scenario],
  );

  const q = useTemplateGallery(query);
  const addM = useAddFromGallery();
  const [toast, setToast] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const items = q.data?.templates ?? [];

  async function handleUse(item: GalleryTemplate) {
    setErrMsg(null);
    try {
      const res = await addM.add(item.id);
      setToast(res.created ? 'Added to your templates' : 'Already in your list');
      window.setTimeout(() => setToast(null), 3200);
    } catch {
      setErrMsg('Could not add that template. Please try again.');
      window.setTimeout(() => setErrMsg(null), 4000);
    }
  }

  // Build the pack/channel/scenario filter options. Prefer the server-provided
  // pack list (so a new pack appears without a UI change); fall back to labels.
  const packOptions = (q.data?.packs ?? Object.keys(PACK_LABELS)).map((p) => ({
    value: p,
    label: packLabel(p),
  }));
  const scenarioOptions = (q.data?.scenarios ?? Object.keys(SCENARIO_LABELS)).map((s) => ({
    value: s,
    label: scenarioLabel(s),
  }));

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal modal--gallery">
        <div className="modal__head">
          <h3>Browse templates</h3>
          <button className="x" onClick={onClose} aria-label="Close"><Icon d={ClosePath} /></button>
        </div>

        <div className="gal-filters">
          <FilterRow label="Industry" options={packOptions} active={pack} onPick={setPack} />
          <FilterRow
            label="Channel"
            options={[
              { value: 'email', label: 'Email' },
              { value: 'sms', label: 'SMS' },
            ]}
            active={channel}
            onPick={setChannel}
          />
          <FilterRow label="When to send" options={scenarioOptions} active={scenario} onPick={setScenario} />
        </div>

        <div className="modal__body gal-body">
          {q.isLoading ? (
            <p className="muted" style={{ fontSize: 13 }}>Loading the template library...</p>
          ) : q.isError ? (
            <p className="muted" style={{ fontSize: 13 }}>
              We could not load the template library right now. Please close this and try again.
            </p>
          ) : items.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>
              No templates match those filters. Try clearing one.
            </p>
          ) : (
            <>
              <p className="muted" style={{ fontSize: 12.5, margin: '0 0 14px' }}>
                {items.length} ready-made {items.length === 1 ? 'template' : 'templates'}. Pick one and it lands in your list, ready to edit.
              </p>
              <div className="gal-grid">
                {items.map((item) => (
                  <GalleryItemCard
                    key={item.id}
                    item={item}
                    onUse={handleUse}
                    adding={addM.isPending && addM.pendingId === item.id}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {toast && (
        <div className="celebrate-toast" role="status" aria-live="polite">
          <span className="celebrate-emoji" aria-hidden>{'✓'}</span>
          <span className="celebrate-msg">{toast}</span>
        </div>
      )}
      {errMsg && (
        <div className="celebrate-toast gal-toast--err" role="status" aria-live="polite">
          <span className="celebrate-msg">{errMsg}</span>
        </div>
      )}
    </div>
  );
}
